import React, { useEffect, useMemo, useState } from 'react';
import './Dropdown.css';
import { apiCall } from '../utils/auth';

const formatBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const COR = 'rgb(235, 133, 0)';

const num = (v) => Number(v || 0) || 0;
const formatData = (v) => {
  const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (v || '—');
};

// Serviços terceiros por produção: escolhe a produção no grid e vê, num pop-up, lado a lado, os serviços
// previstos no orçamento (à esquerda) e os contratados na produção (o real, à direita), cada um em card, com os totais.
// Aberto pela Gestão Financeira ("Ver mais" de Serv. Terceiros) com ?producaoId=ID já abre o pop-up.
export default function ResumoServicosTerceirosDropdown() {
  const [producoes, setProducoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [filtro, setFiltro] = useState('');
  // Campo do filtro: 'todos' | 'producao' | 'orcamento' | 'cliente'
  const [campoFiltro, setCampoFiltro] = useState('todos');

  const [aberta, setAberta] = useState(null);
  const [resumo, setResumo] = useState(null);
  const [carregandoResumo, setCarregandoResumo] = useState(false);
  const [erroResumo, setErroResumo] = useState('');

  const idDe = (p) => p?.producaoId ?? p?.ProducaoId ?? p?.id ?? p?.Id;
  const orcamentoDe = (p) => p?.orcamentoId ?? p?.OrcamentoId;

  useEffect(() => {
    carregarProducoes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregarProducoes = async () => {
    try {
      setLoading(true);
      const data = await apiCall('Producoes');
      const lista = Array.isArray(data) ? data : [];
      setProducoes(lista);
      setErro('');

      // Vindo da Gestão Financeira (?producaoId=ID): abre direto o pop-up dela
      const id = new URLSearchParams(window.location.search).get('producaoId');
      if (id) {
        const p = lista.find(x => String(idDe(x)) === String(id));
        abrirResumo(p || { producaoId: id });
      }
    } catch (e) {
      console.error('Erro ao carregar projetos:', e);
      setErro('Erro ao carregar os projetos.');
      setProducoes([]);
    } finally {
      setLoading(false);
    }
  };

  const abrirResumo = async (p) => {
    setAberta(p);
    setResumo(null);
    setErroResumo('');
    setCarregandoResumo(true);
    try {
      const producaoId = idDe(p);
      const orcamentoId = orcamentoDe(p);
      const [todos, doOrcamento, fornecedores] = await Promise.all([
        apiCall('ServicosTerceiros'),
        orcamentoId ? apiCall(`ServicosTerceiros/orcamento/${orcamentoId}`) : Promise.resolve([]),
        apiCall('Fornecedor').catch(() => [])
      ]);

      const nomeFornecedor = {};
      (Array.isArray(fornecedores) ? fornecedores : []).forEach(f => {
        const id = f.id ?? f.Id ?? f.fornecedorId;
        if (id != null) nomeFornecedor[id] = f.nomeFornecedor ?? f.NomeFornecedor ?? f.nome ?? f.razaoSocial;
      });
      const montar = (s) => {
        const fornecedorId = s.fornecedorId ?? s.FornecedorId;
        return {
          id: s.id ?? s.Id,
          descricao: s.descricao ?? s.Descricao ?? 'Sem descrição',
          fornecedor: (fornecedorId != null && nomeFornecedor[fornecedorId]) || (fornecedorId != null ? `Fornecedor #${fornecedorId}` : '—'),
          tipo: s.tipoServicoNome ?? s.TipoServicoNome ?? '—',
          data: s.dataEntrega ?? s.DataEntrega,
          status: s.status ?? s.Status ?? '—',
          valor: num(s.valor ?? s.Valor)
        };
      };

      // Previsto: serviços do orçamento (sem produção). Real: os da produção (sem orçamento).
      const previstos = (Array.isArray(doOrcamento) ? doOrcamento : [])
        .filter(s => (s.producaoId ?? s.ProducaoId) == null)
        .map(montar);
      const reais = (Array.isArray(todos) ? todos : [])
        .filter(s => String(s.producaoId ?? s.ProducaoId ?? '') === String(producaoId) && (s.orcamentoId ?? s.OrcamentoId) == null)
        .map(montar);

      setResumo({ orcamentoId, previstos, reais });
    } catch (e) {
      console.error('Erro ao carregar serviços terceiros do projeto:', e);
      setErroResumo('Não foi possível carregar os serviços terceiros.');
    } finally {
      setCarregandoResumo(false);
    }
  };

  const fecharResumo = () => {
    setAberta(null);
    setResumo(null);
  };

  const filtradas = useMemo(() => {
    const termo = filtro.trim().toLowerCase();
    if (!termo) return producoes;
    const contem = (v) => String(v ?? '').toLowerCase().includes(termo);
    return producoes.filter(p => {
      if (campoFiltro === 'producao') return String(idDe(p) ?? '') === termo;
      if (campoFiltro === 'orcamento') return String(orcamentoDe(p) ?? '') === termo;
      if (campoFiltro === 'cliente') return contem(p.clienteNome ?? p.ClienteNome);
      return [
        idDe(p), p.titulo ?? p.Titulo, p.clienteNome ?? p.ClienteNome, p.status ?? p.Status,
        p.contratosId ?? p.ContratosId, orcamentoDe(p)
      ].some(contem);
    });
  }, [producoes, filtro, campoFiltro]);

  const somaValor = (lista) => lista.reduce((s, x) => s + x.valor, 0);

  const Card = ({ s }) => (
    <div className="resp-card">
      <div className="resp-card-nome">{s.descricao}</div>
      <div className="resp-card-linha"><span>Fornecedor</span><strong>{s.fornecedor}</strong></div>
      <div className="resp-card-linha"><span>Tipo de serviço</span><strong>{s.tipo}</strong></div>
      <div className="resp-card-linha"><span>Data</span><strong>{formatData(s.data)}</strong></div>
      <div className="resp-card-linha"><span>Status</span><strong>{s.status}</strong></div>
      <div className="resp-card-linha resp-card-total"><span>Valor</span><strong>{formatBRL(s.valor)}</strong></div>
    </div>
  );

  return (
    <div className="container-geral fundo-branco" style={{ minHeight: '100vh', background: '#f0f0f0', padding: 20 }}>
      <h2 style={{ textAlign: 'center', color: COR, marginBottom: 20 }}>🤝 Serviços Terceiros por Projeto</h2>

      <div className="filtro-container">
        <div className="filtro-wrapper">
          <input
            type={campoFiltro === 'producao' || campoFiltro === 'orcamento' ? 'number' : 'text'}
            placeholder={{
              todos: 'Buscar por ID, título, cliente, status, contrato ou orçamento...',
              producao: 'Digite o ID do projeto...',
              orcamento: 'Digite o ID do orçamento...',
              cliente: 'Digite o nome do cliente...'
            }[campoFiltro]}
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            className="filtro-input"
          />
          <select
            value={campoFiltro}
            onChange={(e) => { setCampoFiltro(e.target.value); setFiltro(''); }}
            className="materia-prima-filtro-campo"
            aria-label="Filtrar por"
          >
            <option value="todos">Todos os campos</option>
            <option value="producao">Projeto ID</option>
            <option value="orcamento">Orçamento ID</option>
            <option value="cliente">Nome do cliente</option>
          </select>
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Carregando projetos...</div>}
      {erro && <div style={{ textAlign: 'center', padding: 20, color: '#b00020', background: '#fdecea', borderRadius: 6, margin: '20px 0' }}>{erro}</div>}

      {!loading && !erro && (
        filtradas.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#666', background: 'white', marginTop: 20, borderRadius: 10 }}>
            {filtro ? 'Nenhum projeto encontrado para a busca' : 'Nenhum projeto cadastrado'}
          </div>
        ) : (
          <div className="materia-prima-producoes-grid">
            {filtradas.map((p, idx) => {
              const id = idDe(p);
              const status = p.statusNome ?? p.StatusNome ?? p.status ?? p.Status ?? '';
              return (
                <button type="button" key={id ?? idx} className="materia-prima-producao-card" onClick={() => abrirResumo(p)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: COR }}>Projeto #{id}</span>
                    {status && <span className="materia-prima-producao-status">{status}</span>}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#333', lineHeight: 1.3 }}>{p.titulo ?? p.Titulo ?? 'Sem título'}</div>
                  <div style={{ fontSize: 13, color: '#666', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span><strong>Cliente:</strong> {p.clienteNome ?? p.ClienteNome ?? '—'}</span>
                    <span><strong>Contrato:</strong> {p.contratosId ?? p.ContratosId ?? '—'} · <strong>Orçamento:</strong> {orcamentoDe(p) ?? '—'}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )
      )}

      {aberta && (
        <div className="modal-overlay" onClick={fecharResumo} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95000, padding: 16 }}>
          <div className="modal materia-prima-resumo-modal resp-modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, color: COR }}>Serviços Terceiros · Projeto #{idDe(aberta)}</h3>
                <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                  {aberta.titulo ?? aberta.Titulo ?? ''}
                  {resumo?.orcamentoId ? ` · Orçamento #${resumo.orcamentoId}` : ''}
                </div>
              </div>
              <button onClick={fecharResumo} aria-label="Fechar" title="Fechar" className="materia-prima-fechar" style={{ backgroundColor: COR }}>×</button>
            </div>

            {carregandoResumo && <div style={{ textAlign: 'center', padding: 30, color: '#666' }}>Carregando serviços terceiros...</div>}
            {erroResumo && <div style={{ textAlign: 'center', padding: 16, color: '#b00020', background: '#fdecea', borderRadius: 6 }}>{erroResumo}</div>}

            {resumo && !carregandoResumo && (() => {
              const totalPrevisto = somaValor(resumo.previstos);
              const totalReal = somaValor(resumo.reais);
              const diferenca = totalReal - totalPrevisto;
              return (
                <>
                  <div className="resp-lado-a-lado">
                    {/* Esquerda: serviços previstos no orçamento */}
                    <div className="resp-coluna resp-coluna-previsto">
                      <h4>📋 Previsto no orçamento</h4>
                      <div className="resp-lista">
                        {resumo.previstos.length === 0 ? (
                          <div className="materia-prima-resumo-vazio">
                            {resumo.orcamentoId ? 'O orçamento não tem serviços terceiros.' : 'Este projeto não tem orçamento vinculado.'}
                          </div>
                        ) : resumo.previstos.map((s, i) => <Card key={s.id ?? i} s={s} />)}
                      </div>
                      <div className="resp-coluna-rodape">
                        <div className="resp-card-linha"><span>Serviços previstos</span><strong>{resumo.previstos.length}</strong></div>
                        <div className="resp-card-linha"><span>Total valor previsto</span><strong>{formatBRL(totalPrevisto)}</strong></div>
                      </div>
                    </div>

                    {/* Direita: serviços contratados no projeto (real) */}
                    <div className="resp-coluna resp-coluna-real">
                      <h4>🔨 Real no projeto</h4>
                      <div className="resp-lista">
                        {resumo.reais.length === 0 ? (
                          <div className="materia-prima-resumo-vazio">Nenhum serviço terceiro adicionado neste projeto ainda.</div>
                        ) : resumo.reais.map((s, i) => <Card key={s.id ?? i} s={s} />)}
                      </div>
                      <div className="resp-coluna-rodape">
                        <div className="resp-card-linha"><span>Serviços no projeto</span><strong>{resumo.reais.length}</strong></div>
                        <div className="resp-card-linha"><span>Total valor real</span><strong>{formatBRL(totalReal)}</strong></div>
                      </div>
                    </div>
                  </div>

                  {/* Comparação dos dois lados */}
                  <div className="resp-comparacao" style={{ color: diferenca > 0 ? '#c62828' : '#2e7d32' }}>
                    {diferenca > 0
                      ? `O real passou ${formatBRL(diferenca)} do previsto`
                      : diferenca < 0
                        ? `O real ficou ${formatBRL(-diferenca)} abaixo do previsto`
                        : 'Real igual ao previsto'}
                    {' · '}{resumo.reais.length} serviço(s) no projeto de {resumo.previstos.length} previsto(s)
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
